import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import mcbLogo from "../images/mcb-logo.png";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4040/api";
const TERMS_VERSION = "2026-08-12";
const DRAFT_KEY = "shipstock_apply_draft_v1";

const INPUT_CLASS =
  "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 outline-none transition focus:border-slate-950 focus:ring-2 focus:ring-slate-950/10";

const STEPS = [
  { key: "business", label: "Business" },
  { key: "contacts", label: "Contacts" },
  { key: "directors", label: "Directors / Owners" },
  { key: "info", label: "Business info" },
  { key: "references", label: "References & stock" },
  { key: "declaration", label: "Review & sign" },
];

const ENTITY_TYPES = ["Company", "Sole trader", "Partnership", "Trust", "Other"];
const BUSINESS_TYPES = ["Farming – sheep/beef", "Dairy", "Stock agent", "Processor", "Other"];

const emptyAddress = () => ({ street: "", city: "", postcode: "" });
const emptyDirector = () => ({ name: "", mobile: "", email: "" });
const emptyReference = () => ({ company: "", contactName: "", address: "", phone: "", email: "" });

const initialForm = () => ({
  entityType: "Company",
  legalName: "",
  tradingAs: "",
  companyNumber: "",
  nzbn: "",
  registrationDate: "",
  businessType: "",
  businessTypeOther: "",
  postalAddress: emptyAddress(),
  physicalSame: true,
  physicalAddress: emptyAddress(),
  phone: "",
  email: "",
  estMonthlySpend: "",

  purchasing: { name: "", email: "" },
  accounts: { name: "", email: "" },
  invoiceEmail: "",
  accountsPayable: { name: "", phone: "", email: "" },

  directors: [emptyDirector()],

  notGstRegistered: false,
  gstNumber: "",
  creditLimitRequested: "",
  bank: { name: "", branch: "" },
  accountant: { firm: "", contact: "", phone: "" },

  tradeReferences: [emptyReference(), emptyReference()],

  livestock: {
    naitNumber: "",
    farmName: "",
    island: "",
    stockTypes: [],
    estHeadPerYear: "",
    stockAgent: "",
  },

  signerName: "",
  signerTitle: "",
  consents: {
    creditEnquiries: false,
    infoCorrect: false,
    termsAccepted: false,
    ppsa: false,
    authorised: false,
    ownersRisk: false,
    cgaExclusion: false,
    privacy: false,
  },

  website: "", // honeypot — real people never fill this in
});

const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());
const filled = (v) => String(v ?? "").trim().length > 0;

function validateStep(step, f) {
  const e = [];
  const req = (v, label) => { if (!filled(v)) e.push(`${label} is required`); };
  const reqEmail = (v, label) => {
    if (!filled(v)) e.push(`${label} is required`);
    else if (!isEmail(v)) e.push(`${label} must be a valid email address`);
  };

  if (step === 0) {
    req(f.legalName, "Legal entity name");
    if (f.entityType === "Company") {
      req(f.companyNumber, "Company number");
      req(f.registrationDate, "Date of registration");
    }
    if (filled(f.nzbn) && !/^\d{13}$/.test(f.nzbn.trim())) e.push("NZBN must be 13 digits");
    req(f.businessType, "Type of business");
    if (f.businessType === "Other") req(f.businessTypeOther, "Please describe your type of business");
    req(f.postalAddress.street, "Postal address");
    req(f.postalAddress.city, "Postal city / town");
    if (!f.physicalSame) {
      req(f.physicalAddress.street, "Physical address");
      req(f.physicalAddress.city, "Physical city / town");
    }
    req(f.phone, "Business phone");
    reqEmail(f.email, "Business email");
    if (!filled(f.estMonthlySpend) || Number(f.estMonthlySpend) <= 0) e.push("Estimated monthly spend is required");
  }

  if (step === 1) {
    req(f.purchasing.name, "Purchasing contact name");
    reqEmail(f.purchasing.email, "Purchasing contact email");
    req(f.accounts.name, "Accounts contact name");
    reqEmail(f.accounts.email, "Accounts contact email");
    reqEmail(f.invoiceEmail, "Email for invoices and statements");
    if (filled(f.accountsPayable.email) && !isEmail(f.accountsPayable.email)) e.push("Accounts payable email must be a valid email address");
  }

  if (step === 2) {
    f.directors.forEach((d, i) => {
      req(d.name, `Director / owner ${i + 1} name`);
      req(d.mobile, `Director / owner ${i + 1} mobile`);
      reqEmail(d.email, `Director / owner ${i + 1} email`);
    });
  }

  if (step === 3) {
    if (!f.notGstRegistered) req(f.gstNumber, "GST registration number");
    if (!filled(f.creditLimitRequested) || Number(f.creditLimitRequested) <= 0) e.push("Credit limit applied for is required");
    req(f.bank.name, "Bank name");
    req(f.bank.branch, "Bank branch");
  }

  if (step === 4) {
    f.tradeReferences.forEach((r, i) => {
      const touched = Object.values(r).some(filled);
      if (i < 2 || touched) {
        req(r.company, `Trade reference ${i + 1} company`);
        req(r.contactName, `Trade reference ${i + 1} contact name`);
        req(r.phone, `Trade reference ${i + 1} phone`);
        if (filled(r.email) && !isEmail(r.email)) e.push(`Trade reference ${i + 1} email must be a valid email address`);
      }
    });
  }

  return e;
}

export default function Apply() {
  const [form, setForm] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
      if (saved?.form) return { ...initialForm(), ...saved.form, website: "" };
    } catch (err) { /* no draft */ }
    return initialForm();
  });
  const [step, setStep] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
      if (Number.isInteger(saved?.step)) return Math.min(Math.max(saved.step, 0), STEPS.length - 1);
    } catch (err) { /* no draft */ }
    return 0;
  });
  const [signature, setSignature] = useState("");
  const [errors, setErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // { reference }
  const topRef = useRef(null);

  // Save progress so a refresh doesn't lose work (signature is never stored).
  useEffect(() => {
    if (submitted) return;
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify({ form, step })); } catch (err) { /* storage blocked */ }
  }, [form, step, submitted]);

  const update = (key, value) => setForm((p) => ({ ...p, [key]: value }));
  const updateNested = (key, sub, value) => setForm((p) => ({ ...p, [key]: { ...p[key], [sub]: value } }));
  const updateList = (key, i, sub, value) =>
    setForm((p) => ({ ...p, [key]: p[key].map((item, idx) => (idx === i ? { ...item, [sub]: value } : item)) }));

  const scrollTop = () => topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const next = () => {
    const e = validateStep(step, form);
    setErrors(e);
    if (e.length) return scrollTop();
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    setTimeout(scrollTop, 0);
  };
  const back = () => {
    setErrors([]);
    setStep((s) => Math.max(s - 1, 0));
    setTimeout(scrollTop, 0);
  };

  const submit = async (ev) => {
    ev.preventDefault();
    // Re-check every step, then the declaration step itself.
    for (let i = 0; i < STEPS.length - 1; i += 1) {
      const e = validateStep(i, form);
      if (e.length) { setStep(i); setErrors(e); return scrollTop(); }
    }
    const e = [];
    if (!filled(form.signerName)) e.push("Full name of signatory is required");
    if (!filled(form.signerTitle)) e.push("Position / title is required");
    if (!signature) e.push("Please draw your signature");
    if (!Object.values(form.consents).every(Boolean)) e.push("All declarations must be ticked before you can submit");
    setErrors(e);
    if (e.length) return scrollTop();

    try {
      setSubmitting(true);
      const res = await fetch(`${API_BASE}/applications`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, signature, termsVersion: TERMS_VERSION }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Could not submit your application. Please try again.");
      try { localStorage.removeItem(DRAFT_KEY); } catch (err) { /* ignore */ }
      setSubmitted({ reference: data.reference || "" });
      setTimeout(scrollTop, 0);
    } catch (err) {
      setErrors([err.message]);
      scrollTop();
    } finally {
      setSubmitting(false);
    }
  };

  const startOver = () => {
    if (!window.confirm("Clear everything you've entered and start again?")) return;
    try { localStorage.removeItem(DRAFT_KEY); } catch (err) { /* ignore */ }
    setForm(initialForm());
    setSignature("");
    setErrors([]);
    setStep(0);
  };

  const isCompany = form.entityType === "Company";
  const ownerWord = isCompany ? "Director" : "Owner";

  if (submitted) {
    return (
      <Shell topRef={topRef}>
        <div className="rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-xl text-emerald-700">✓</div>
          <h1 className="text-2xl font-bold text-slate-950">Application received</h1>
          <p className="mx-auto mt-3 max-w-md text-sm text-slate-600">
            Thanks {form.signerName.split(" ")[0] || ""} — we've emailed a copy of your signed application to{" "}
            <strong>{form.purchasing.email || form.email}</strong>. Our accounts team will check your references and be in touch.
          </p>
          {submitted.reference && (
            <p className="mt-4 text-xs text-slate-500">
              Reference: <span className="font-mono font-semibold text-slate-800">{submitted.reference}</span>
            </p>
          )}
        </div>
      </Shell>
    );
  }

  return (
    <Shell topRef={topRef}>
      {/* Progress */}
      <div className="mb-6">
        <div className="mb-2 flex items-center justify-between text-xs font-semibold text-slate-500">
          <span>Step {step + 1} of {STEPS.length} · {STEPS[step].label}</span>
          <button type="button" onClick={startOver} className="font-medium text-slate-400 hover:text-slate-700">Start over</button>
        </div>
        <div className="grid grid-cols-6 gap-1.5">
          {STEPS.map((s, i) => (
            <div key={s.key} className={`h-1.5 rounded-full ${i <= step ? "bg-slate-950" : "bg-slate-200"}`} />
          ))}
        </div>
        <div className="mt-3 hidden grid-cols-6 gap-1.5 text-[11px] font-medium text-slate-400 md:grid">
          {STEPS.map((s, i) => (
            <span key={s.key} className={i === step ? "text-slate-900" : ""}>{s.label}</span>
          ))}
        </div>
      </div>

      {errors.length > 0 && (
        <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
          <p className="mb-1 font-semibold">Please fix the following:</p>
          <ul className="list-disc space-y-0.5 pl-5">
            {errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      <form onSubmit={submit} className="space-y-6" noValidate>
        {/* Honeypot — hidden from people, bots fill it */}
        <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", height: 0, overflow: "hidden" }}>
          <label>Website<input tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => update("website", e.target.value)} /></label>
        </div>

        {/* ── STEP 1 ── */}
        {step === 0 && (
          <>
            <Card title="Business Details" description="Tell us about the entity that will hold the account.">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Field label="Entity Type *">
                  <select className={INPUT_CLASS} value={form.entityType} onChange={(e) => update("entityType", e.target.value)}>
                    {ENTITY_TYPES.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="Legal Entity Name *">
                  <input className={INPUT_CLASS} value={form.legalName} onChange={(e) => update("legalName", e.target.value)} />
                </Field>
                <Field label="Trading As">
                  <input className={INPUT_CLASS} value={form.tradingAs} onChange={(e) => update("tradingAs", e.target.value)} />
                </Field>
                <Field label="Type of Business *">
                  <select className={INPUT_CLASS} value={form.businessType} onChange={(e) => update("businessType", e.target.value)}>
                    <option value="">— Select —</option>
                    {BUSINESS_TYPES.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </Field>
                {form.businessType === "Other" && (
                  <div className="md:col-span-2">
                    <Field label="Describe your business *">
                      <input className={INPUT_CLASS} value={form.businessTypeOther} onChange={(e) => update("businessTypeOther", e.target.value)} />
                    </Field>
                  </div>
                )}
                {isCompany && (
                  <>
                    <Field label="Company Number *">
                      <input className={INPUT_CLASS} value={form.companyNumber} onChange={(e) => update("companyNumber", e.target.value)} />
                    </Field>
                    <Field label="Date of Registration *">
                      <input className={INPUT_CLASS} type="date" value={form.registrationDate} onChange={(e) => update("registrationDate", e.target.value)} />
                    </Field>
                  </>
                )}
                <Field label="NZBN">
                  <input className={INPUT_CLASS} inputMode="numeric" maxLength={13} value={form.nzbn} onChange={(e) => update("nzbn", e.target.value.replace(/\D/g, ""))} placeholder="13 digits (optional)" />
                </Field>
              </div>
            </Card>

            <Card title="Addresses">
              <div className="space-y-5">
                <AddressFields label="Postal Address *" value={form.postalAddress} onChange={(v) => update("postalAddress", v)} />
                <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" className="h-4 w-4 rounded border-slate-300" checked={form.physicalSame} onChange={(e) => update("physicalSame", e.target.checked)} />
                  Physical address is the same as postal
                </label>
                {!form.physicalSame && (
                  <AddressFields label="Physical Address *" value={form.physicalAddress} onChange={(v) => update("physicalAddress", v)} />
                )}
              </div>
            </Card>

            <Card title="Contact & Spend">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <Field label="Business Phone *">
                  <input className={INPUT_CLASS} type="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} />
                </Field>
                <Field label="Business Email *">
                  <input className={INPUT_CLASS} type="email" value={form.email} onChange={(e) => update("email", e.target.value)} />
                </Field>
                <Field label="Estimated Monthly Spend (NZD) *">
                  <input className={INPUT_CLASS} type="number" min="0" value={form.estMonthlySpend} onChange={(e) => update("estMonthlySpend", e.target.value)} placeholder="e.g. 5000" />
                </Field>
              </div>
            </Card>
          </>
        )}

        {/* ── STEP 2 ── */}
        {step === 1 && (
          <Card title="Contacts" description="Who we'll deal with day to day.">
            <div className="space-y-6">
              <ContactBlock title="Purchasing Contact *" note="Becomes the default booking contact and portal user.">
                <Field label="Name"><input className={INPUT_CLASS} value={form.purchasing.name} onChange={(e) => updateNested("purchasing", "name", e.target.value)} /></Field>
                <Field label="Email"><input className={INPUT_CLASS} type="email" value={form.purchasing.email} onChange={(e) => updateNested("purchasing", "email", e.target.value)} /></Field>
              </ContactBlock>

              <ContactBlock title="Accounts Contact *">
                <Field label="Name"><input className={INPUT_CLASS} value={form.accounts.name} onChange={(e) => updateNested("accounts", "name", e.target.value)} /></Field>
                <Field label="Email"><input className={INPUT_CLASS} type="email" value={form.accounts.email} onChange={(e) => updateNested("accounts", "email", e.target.value)} /></Field>
              </ContactBlock>

              <div>
                <Field label="Email for Invoices and Statements *">
                  <input className={INPUT_CLASS} type="email" value={form.invoiceEmail} onChange={(e) => update("invoiceEmail", e.target.value)} />
                </Field>
              </div>

              <ContactBlock title="Accounts Payable Contact" note="Optional — only if different from the accounts contact.">
                <Field label="Name"><input className={INPUT_CLASS} value={form.accountsPayable.name} onChange={(e) => updateNested("accountsPayable", "name", e.target.value)} /></Field>
                <Field label="Phone"><input className={INPUT_CLASS} type="tel" value={form.accountsPayable.phone} onChange={(e) => updateNested("accountsPayable", "phone", e.target.value)} /></Field>
                <Field label="Email"><input className={INPUT_CLASS} type="email" value={form.accountsPayable.email} onChange={(e) => updateNested("accountsPayable", "email", e.target.value)} /></Field>
              </ContactBlock>
            </div>
          </Card>
        )}

        {/* ── STEP 3 ── */}
        {step === 2 && (
          <Card title={`${ownerWord}s / Owners`} description={isCompany ? "Add every director of the company." : "Add every owner, partner or trustee."}>
            <div className="space-y-4">
              {form.directors.map((d, i) => (
                <div key={i} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                  <div className="mb-4 flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-700">{ownerWord} {i + 1}</span>
                    {form.directors.length > 1 && (
                      <button type="button" className="text-xs font-semibold text-red-500 hover:text-red-700"
                        onClick={() => update("directors", form.directors.filter((_, idx) => idx !== i))}>Remove</button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <Field label="Name *"><input className={INPUT_CLASS} value={d.name} onChange={(e) => updateList("directors", i, "name", e.target.value)} /></Field>
                    <Field label="Mobile *"><input className={INPUT_CLASS} type="tel" value={d.mobile} onChange={(e) => updateList("directors", i, "mobile", e.target.value)} /></Field>
                    <Field label="Email *"><input className={INPUT_CLASS} type="email" value={d.email} onChange={(e) => updateList("directors", i, "email", e.target.value)} /></Field>
                  </div>
                </div>
              ))}
              <button type="button" onClick={() => update("directors", [...form.directors, emptyDirector()])}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                + Add another {ownerWord.toLowerCase()} / owner
              </button>
            </div>
          </Card>
        )}

        {/* ── STEP 4 ── */}
        {step === 3 && (
          <>
            <Card title="Business Information">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Field label={`GST Registration Number${form.notGstRegistered ? "" : " *"}`}>
                  <input className={INPUT_CLASS} value={form.gstNumber} disabled={form.notGstRegistered}
                    onChange={(e) => update("gstNumber", e.target.value)} placeholder="e.g. 123-456-789" />
                  <label className="mt-2 flex cursor-pointer items-center gap-2 text-sm text-slate-600">
                    <input type="checkbox" className="h-4 w-4 rounded border-slate-300" checked={form.notGstRegistered}
                      onChange={(e) => setForm((p) => ({ ...p, notGstRegistered: e.target.checked, gstNumber: e.target.checked ? "" : p.gstNumber }))} />
                    Not GST registered
                  </label>
                </Field>
                <Field label="Credit Limit Applied For (NZD) *">
                  <input className={INPUT_CLASS} type="number" min="0" value={form.creditLimitRequested} onChange={(e) => update("creditLimitRequested", e.target.value)} />
                </Field>
              </div>
            </Card>

            <Card title="Banking" description="Bank name and branch only — please don't enter account numbers.">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Field label="Bank Name *"><input className={INPUT_CLASS} value={form.bank.name} onChange={(e) => updateNested("bank", "name", e.target.value)} /></Field>
                <Field label="Branch *"><input className={INPUT_CLASS} value={form.bank.branch} onChange={(e) => updateNested("bank", "branch", e.target.value)} /></Field>
              </div>
            </Card>

            <Card title="Accountants" description="Optional.">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <Field label="Firm"><input className={INPUT_CLASS} value={form.accountant.firm} onChange={(e) => updateNested("accountant", "firm", e.target.value)} /></Field>
                <Field label="Contact"><input className={INPUT_CLASS} value={form.accountant.contact} onChange={(e) => updateNested("accountant", "contact", e.target.value)} /></Field>
                <Field label="Phone"><input className={INPUT_CLASS} type="tel" value={form.accountant.phone} onChange={(e) => updateNested("accountant", "phone", e.target.value)} /></Field>
              </div>
            </Card>
          </>
        )}

        {/* ── STEP 5 ── */}
        {step === 4 && (
          <>
            <Card title="Trade References" description="At least two, up to three. A contact name and email helps us check them quickly.">
              <div className="space-y-4">
                {form.tradeReferences.map((r, i) => (
                  <div key={i} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                    <div className="mb-4 flex items-center justify-between">
                      <span className="text-sm font-semibold text-slate-700">Reference {i + 1}{i < 2 ? " *" : " (optional)"}</span>
                      {i >= 2 && (
                        <button type="button" className="text-xs font-semibold text-red-500 hover:text-red-700"
                          onClick={() => update("tradeReferences", form.tradeReferences.filter((_, idx) => idx !== i))}>Remove</button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <Field label="Company"><input className={INPUT_CLASS} value={r.company} onChange={(e) => updateList("tradeReferences", i, "company", e.target.value)} /></Field>
                      <Field label="Contact Name"><input className={INPUT_CLASS} value={r.contactName} onChange={(e) => updateList("tradeReferences", i, "contactName", e.target.value)} /></Field>
                      <Field label="Phone"><input className={INPUT_CLASS} type="tel" value={r.phone} onChange={(e) => updateList("tradeReferences", i, "phone", e.target.value)} /></Field>
                      <Field label="Email"><input className={INPUT_CLASS} type="email" value={r.email} onChange={(e) => updateList("tradeReferences", i, "email", e.target.value)} /></Field>
                      <div className="md:col-span-2">
                        <Field label="Address"><input className={INPUT_CLASS} value={r.address} onChange={(e) => updateList("tradeReferences", i, "address", e.target.value)} /></Field>
                      </div>
                    </div>
                  </div>
                ))}
                {form.tradeReferences.length < 3 && (
                  <button type="button" onClick={() => update("tradeReferences", [...form.tradeReferences, emptyReference()])}
                    className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                    + Add a third reference
                  </button>
                )}
              </div>
            </Card>

            <Card title="Livestock Profile" description="Helps us plan capacity. All optional.">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Field label="NAIT Number(s)"><input className={INPUT_CLASS} value={form.livestock.naitNumber} onChange={(e) => updateNested("livestock", "naitNumber", e.target.value)} /></Field>
                <Field label="Farm Name"><input className={INPUT_CLASS} value={form.livestock.farmName} onChange={(e) => updateNested("livestock", "farmName", e.target.value)} /></Field>
                <Field label="Island">
                  <select className={INPUT_CLASS} value={form.livestock.island} onChange={(e) => updateNested("livestock", "island", e.target.value)}>
                    <option value="">— Select —</option>
                    <option>Chatham</option>
                    <option>Pitt</option>
                    <option>Mainland</option>
                  </select>
                </Field>
                <Field label="Estimated Head Per Year">
                  <input className={INPUT_CLASS} type="number" min="0" value={form.livestock.estHeadPerYear} onChange={(e) => updateNested("livestock", "estHeadPerYear", e.target.value)} />
                </Field>
                <div className="md:col-span-2">
                  <Field label="Stock Types">
                    <div className="flex flex-wrap gap-4">
                      {["Cattle", "Sheep", "Other"].map((t) => (
                        <label key={t} className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                          <input type="checkbox" className="h-4 w-4 rounded border-slate-300"
                            checked={form.livestock.stockTypes.includes(t)}
                            onChange={(e) => updateNested("livestock", "stockTypes",
                              e.target.checked ? [...form.livestock.stockTypes, t] : form.livestock.stockTypes.filter((x) => x !== t))} />
                          {t}
                        </label>
                      ))}
                    </div>
                  </Field>
                </div>
                <div className="md:col-span-2">
                  <Field label="Stock Agent">
                    <input className={INPUT_CLASS} value={form.livestock.stockAgent} onChange={(e) => updateNested("livestock", "stockAgent", e.target.value)} placeholder="If bookings come through an agent" />
                  </Field>
                </div>
              </div>
            </Card>
          </>
        )}

        {/* ── STEP 6 ── */}
        {step === 5 && (
          <>
            <Card title="Review Your Application" description="Check everything below. Use Back to change anything.">
              <dl className="grid grid-cols-1 gap-x-8 gap-y-4 md:grid-cols-2">
                <Row label="Entity" value={`${form.legalName}${form.tradingAs ? ` (t/a ${form.tradingAs})` : ""} · ${form.entityType}`} />
                <Row label="Business type" value={form.businessType === "Other" ? form.businessTypeOther : form.businessType} />
                {isCompany && <Row label="Company no. / registered" value={`${form.companyNumber} · ${form.registrationDate}`} />}
                <Row label="Postal address" value={addr(form.postalAddress)} />
                <Row label="Physical address" value={form.physicalSame ? "Same as postal" : addr(form.physicalAddress)} />
                <Row label="Phone / email" value={`${form.phone} · ${form.email}`} />
                <Row label="Est. monthly spend" value={`$${Number(form.estMonthlySpend || 0).toLocaleString()}`} />
                <Row label="Credit limit applied for" value={`$${Number(form.creditLimitRequested || 0).toLocaleString()}`} />
                <Row label="Purchasing contact" value={`${form.purchasing.name} · ${form.purchasing.email}`} />
                <Row label="Accounts contact" value={`${form.accounts.name} · ${form.accounts.email}`} />
                <Row label="Invoices & statements" value={form.invoiceEmail} />
                <Row label="GST" value={form.notGstRegistered ? "Not GST registered" : form.gstNumber} />
                <Row label="Bank" value={`${form.bank.name}, ${form.bank.branch}`} />
                <Row label={`${ownerWord}s / owners`} value={form.directors.map((d) => d.name).join(", ")} />
                <Row label="Trade references" value={form.tradeReferences.filter((r) => r.company).map((r) => r.company).join(", ")} />
              </dl>
            </Card>

            <Card title="Declarations" description="All of the following must be ticked.">
              <div className="space-y-3">
                <Consent id="creditEnquiries" form={form} setForm={setForm}
                  title="Credit enquiries"
                  desc="I authorise McCallum Bros to make credit enquiries about the applicant, including with credit reporting agencies and the trade references provided." />
                <Consent id="infoCorrect" form={form} setForm={setForm}
                  title="Information is correct"
                  desc="The information provided in this application is true and correct." />
                <Consent id="termsAccepted" form={form} setForm={setForm}
                  title="Terms of Trade"
                  desc={<>I have read and agree to the <Link to="/livestock-terms" target="_blank" rel="noopener noreferrer" className="font-semibold underline">Terms of Trade for Carriage of Goods and Livestock</Link> (version {TERMS_VERSION}).</>} />
                <Consent id="ppsa" form={form} setForm={setForm}
                  title="PPSA assent"
                  desc="I assent to McCallum Bros taking a security interest under the Personal Property Securities Act 1999 as set out in the Terms of Trade." />
                <Consent id="authorised" form={form} setForm={setForm}
                  title="Authority to sign"
                  desc="I am authorised to sign this application on behalf of the applicant." />
                <Consent id="ownersRisk" form={form} setForm={setForm}
                  title="Owner's Risk"
                  desc={<span className="italic">"These goods are to be carried at owner's risk. This means that the carrier will not pay compensation if the goods are lost or damaged unless the carrier intentionally loses or damages them."</span>} />
                <Consent id="cgaExclusion" form={form} setForm={setForm}
                  title="Consumer Guarantees Act"
                  desc="The applicant is acquiring these services in trade, and the Consumer Guarantees Act 1993 does not apply (section 43(2))." />
                <Consent id="privacy" form={form} setForm={setForm}
                  title="Privacy"
                  desc="Information provided will be used only for the purposes of obtaining credit reference information." />
              </div>
            </Card>

            <Card title="Signature" description="Electronic signatures are accepted under clause 7.2 of the Terms of Trade.">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Field label="Full Name *"><input className={INPUT_CLASS} value={form.signerName} onChange={(e) => update("signerName", e.target.value)} /></Field>
                <Field label="Position / Title *"><input className={INPUT_CLASS} value={form.signerTitle} onChange={(e) => update("signerTitle", e.target.value)} /></Field>
              </div>
              <div className="mt-4">
                <Field label="Signature *">
                  <SignaturePad onChange={setSignature} />
                </Field>
              </div>
              <p className="mt-3 text-xs text-slate-400">
                Your signature, the date and time, and your device details are recorded when you submit.
              </p>
            </Card>
          </>
        )}

        {/* Nav */}
        <div className="flex items-center justify-between gap-4 pt-2">
          <button type="button" onClick={back} disabled={step === 0 || submitting}
            className="rounded-xl border border-slate-300 bg-white px-6 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:invisible">
            ← Back
          </button>
          {step < STEPS.length - 1 ? (
            <button type="button" onClick={next}
              className="rounded-xl bg-slate-950 px-8 py-3 text-sm font-semibold text-white hover:bg-slate-800">
              Continue →
            </button>
          ) : (
            <button type="submit" disabled={submitting}
              className="rounded-xl bg-slate-950 px-8 py-3 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50">
              {submitting ? "Submitting..." : "Submit Application"}
            </button>
          )}
        </div>
      </form>
    </Shell>
  );
}

const addr = (a) => [a.street, a.city, a.postcode].filter(Boolean).join(", ");

function Shell({ children, topRef }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <div ref={topRef} className="mx-auto max-w-[860px] px-6 py-10 lg:px-8">
        <div className="mb-8">
          <img src={mcbLogo} alt="McCallum Bros Ltd" className="mb-5 h-14 w-auto" />
          <h1 className="text-3xl font-bold tracking-tight text-slate-950">Livestock Shipping Account Application</h1>
        </div>
        {children}
      </div>
    </div>
  );
}

function Card({ title, description, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="rounded-t-2xl border-b border-slate-100 px-6 py-5">
        <h2 className="text-base font-semibold text-slate-950">{title}</h2>
        {description && <p className="mt-0.5 text-sm text-slate-500">{description}</p>}
      </div>
      <div className="px-6 py-5">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</label>
      {children}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</dt>
      <dd className="mt-0.5 break-words text-sm font-medium text-slate-900">{value || "—"}</dd>
    </div>
  );
}

function ContactBlock({ title, note, children }) {
  return (
    <div>
      <p className="text-sm font-semibold text-slate-800">{title}</p>
      {note && <p className="mb-3 text-xs text-slate-500">{note}</p>}
      <div className={`grid grid-cols-1 gap-4 md:grid-cols-2 ${note ? "" : "mt-3"}`}>{children}</div>
    </div>
  );
}

function AddressFields({ label, value, onChange }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div>
      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</label>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-[2fr_1fr_1fr]">
        <input className={INPUT_CLASS} placeholder="Street / PO Box" value={value.street} onChange={(e) => set("street", e.target.value)} />
        <input className={INPUT_CLASS} placeholder="City / town" value={value.city} onChange={(e) => set("city", e.target.value)} />
        <input className={INPUT_CLASS} placeholder="Postcode" value={value.postcode} onChange={(e) => set("postcode", e.target.value)} />
      </div>
    </div>
  );
}

function Consent({ id, form, setForm, title, desc }) {
  const checked = form.consents[id];
  return (
    <label className={`flex cursor-pointer items-start gap-3 rounded-xl border px-4 py-3.5 transition ${checked ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50 hover:bg-slate-100"}`}>
      <input type="checkbox" className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300" checked={checked}
        onChange={(e) => setForm((p) => ({ ...p, consents: { ...p.consents, [id]: e.target.checked } }))} />
      <div>
        <div className={`text-sm font-semibold ${checked ? "text-emerald-800" : "text-slate-800"}`}>{title} *</div>
        <div className={`mt-0.5 text-xs leading-relaxed ${checked ? "text-emerald-700" : "text-slate-500"}`}>{desc}</div>
      </div>
    </label>
  );
}

// Drawn signature — mouse, touch and pen via pointer events. Reports a PNG
// data URL, or "" when blank.
function SignaturePad({ onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0f172a";
  }, []);

  const point = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const down = (e) => {
    e.preventDefault();
    canvasRef.current.setPointerCapture?.(e.pointerId);
    drawing.current = true;
    last.current = point(e);
  };
  const move = (e) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current.getContext("2d");
    const p = point(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (!hasInk) setHasInk(true);
  };
  const up = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onChange(canvasRef.current.toDataURL("image/png"));
  };
  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
    onChange("");
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerLeave={up}
        className="h-40 w-full cursor-crosshair rounded-xl border border-slate-300 bg-white"
        style={{ touchAction: "none" }}
      />
      <div className="mt-1.5 flex items-center justify-between text-xs text-slate-400">
        <span>{hasInk ? "Signature captured" : "Sign above using your mouse, finger or stylus"}</span>
        <button type="button" onClick={clear} className="font-semibold text-slate-500 hover:text-slate-800">Clear</button>
      </div>
    </div>
  );
}